import "./LoginPOS.css";
import POSSessionConfirm from "./POSSessionConfirm";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  api,
  clearPosSession,
  getPosExpiration,
  getPosHeaders,
  getPosSession,
  savePosSession,
} from "./api";


function errorText(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (
    detail &&
    typeof detail.message === "string"
  ) {
    return detail.message;
  }

  if (Array.isArray(detail)) {
    const message = detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");

    if (message) return message;
  }

  return "No fue posible conectar con el servidor.";
}


// Resuelve los archivos públicos desde el backend.
function getPublicAssetUrl(path) {
  if (
    typeof path !== "string" ||
    !path.trim()
  ) {
    return "";
  }

  try {
    const backendUrl = new URL(
      api.defaults.baseURL || "/",
      window.location.origin
    );

    const assetUrl = new URL(
      path.trim(),
      `${backendUrl.origin}/`
    );

    if (
      !["http:", "https:"].includes(
        assetUrl.protocol
      )
    ) {
      return "";
    }

    return assetUrl.href;
  } catch {
    return "";
  }
}


function getBrandColor(value, fallback) {
  if (
    typeof value === "string" &&
    value.trim() &&
    !value.includes("var(") &&
    typeof CSS !== "undefined" &&
    CSS.supports("color", value.trim())
  ) {
    return value.trim();
  }

  return fallback;
}


export default function LoginPOS() {
  const { company } = useParams();

  const navigate = useNavigate();
  const location = useLocation();

  const loginInProgress = useRef(false);

  const [companyData, setCompanyData] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [checkingSession, setCheckingSession] =
    useState(true);

  const [companyError, setCompanyError] =
    useState("");

  const [error, setError] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [saving, setSaving] =
    useState(false);

  const [showPassword, setShowPassword] =
    useState(false);

  const [logoFailed, setLogoFailed] =
    useState(false);

  const [showSessionConfirm, setShowSessionConfirm] =
    useState(false);

  const sessionConfirmResolver = useRef(null);

  const resolveSessionConfirm = useCallback((confirmed) => {
    const resolve = sessionConfirmResolver.current;

    sessionConfirmResolver.current = null;
    setShowSessionConfirm(false);

    resolve?.(confirmed);
  }, []);

  const cancelSessionConfirm = useCallback(() => {
    resolveSessionConfirm(false);
  }, [resolveSessionConfirm]);

  const acceptSessionConfirm = useCallback(() => {
    resolveSessionConfirm(true);
  }, [resolveSessionConfirm]);

  function requestSessionConfirmation() {
    return new Promise((resolve) => {
      sessionConfirmResolver.current = resolve;
      setShowSessionConfirm(true);
    });
  }

  // Cancela la confirmación si cambia la empresa
  // o se abandona el inicio de sesión.
  useEffect(() => {
    setShowSessionConfirm(false);

    return () => {
      const resolve = sessionConfirmResolver.current;

      sessionConfirmResolver.current = null;
      resolve?.(false);
    };
  }, [company]);


  // Cargar la identidad visual de la empresa.
  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    setCompanyData(null);
    setCompanyError("");
    setError("");
    setLogoFailed(false);
    setEmail("");
    setPassword("");
    setShowPassword(false);

    if (!company) {
      setCompanyError(
        "No se ha indicado una empresa."
      );

      setLoading(false);

      return () => controller.abort();
    }

    api
      .get(
        `/companies/by-subdomain/${encodeURIComponent(
          company
        )}/branding`,
        {
          signal: controller.signal,
        }
      )
      .then((response) => {
        if (!controller.signal.aborted) {
          setCompanyData(response.data);
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setCompanyError(
            errorText(requestError)
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [company]);


  // Reutilizar la sesión existente de esta pestaña.
  useEffect(() => {
    const controller = new AbortController();

    setCheckingSession(true);

    async function restoreSession() {
      const session = getPosSession(company);

      if (!session?.access_token) {
        setCheckingSession(false);
        return;
      }

      const expiresAt = getPosExpiration(company);

      if (
        !expiresAt ||
        expiresAt <= Date.now()
      ) {
        clearPosSession(
          company,
          session.access_token
        );

        setCheckingSession(false);
        return;
      }

      try {
        const response = await api.get(
          "/auth/pos/me",
          {
            headers: getPosHeaders(company),
            signal: controller.signal,
          }
        );

        if (controller.signal.aborted) return;

        if (
          response.data.subdomain !== company
        ) {
          setError(
            "La sesión guardada no corresponde a esta empresa."
          );
          return;
        }

        navigate(
          `/${encodeURIComponent(company)}/ventas`,
          {
            replace: true,
          }
        );
      } catch (requestError) {
        if (controller.signal.aborted) return;

        if (
          requestError.response?.status === 401
        ) {
          clearPosSession(
            company,
            session.access_token
          );
        } else {
          setError(
            errorText(requestError)
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setCheckingSession(false);
        }
      }
    }

    restoreSession();

    return () => controller.abort();
  }, [company, navigate]);


  const name =
    companyData?.commercial_name ||
    companyData?.company_name ||
    company ||
    "Empresa";

  const primaryColor = getBrandColor(
    companyData?.primary_color,
    "#1f5d7a"
  );

  const secondaryColor = getBrandColor(
    companyData?.secondary_color,
    primaryColor
  );

  const backgroundColor = getBrandColor(
    companyData?.login_background_color,
    "#eef2ff"
  );

  const logoUrl = getPublicAssetUrl(
    companyData?.logo
  );

  const faviconUrl = getPublicAssetUrl(
    companyData?.favicon
  );

  const sessionExpired =
    new URLSearchParams(
      location.search
    ).get("session") === "expired";


  // Aplicar título y favicon.
  useEffect(() => {
    if (!companyData) return undefined;

    const previousTitle = document.title;

    document.title =
      `${name} | NetLedger POS`;

    let favicon = document.querySelector(
      "link[rel~='icon']"
    );

    const previousHref = favicon
      ? favicon.getAttribute("href")
      : null;

    let createdFavicon = false;

    if (faviconUrl) {
      if (!favicon) {
        favicon = document.createElement("link");
        favicon.rel = "icon";

        document.head.appendChild(favicon);

        createdFavicon = true;
      }

      favicon.href = faviconUrl;
    }

    return () => {
      document.title = previousTitle;

      if (faviconUrl && favicon) {
        if (createdFavicon) {
          favicon.remove();
        } else if (previousHref !== null) {
          favicon.setAttribute(
            "href",
            previousHref
          );
        } else {
          favicon.removeAttribute("href");
        }
      }
    };
  }, [
    companyData,
    name,
    faviconUrl,
  ]);


  async function handleLogin(event) {
    event.preventDefault();

    if (
      loginInProgress.current ||
      saving ||
      checkingSession ||
      !companyData ||
      companyError
    ) {
      return;
    }

    loginInProgress.current = true;

    setSaving(true);
    setError("");

    const credentials = {
      email: email.trim(),
      password,
      subdomain: company,
    };

    try {
      let response;

      try {
        response = await api.post(
          "/auth/login-pos",
          credentials
        );
      } catch (requestError) {
        const detail = requestError.response?.data?.detail;

        const hasActiveSession =
          requestError.response?.status === 409 &&
          detail?.code === "POS_SESSION_ACTIVE";

        if (!hasActiveSession) {
          throw requestError;
        }

        const confirmed = await requestSessionConfirmation();

        if (!confirmed) {
          setError(
            "No se cerró la sesión anterior. " +
            "Puedes volver a intentar cuando quieras."
          );

          return;
        }

        // Reenvía las credenciales para validarlas nuevamente.
        response = await api.post(
          "/auth/login-pos",
          {
            ...credentials,
            replace_session: true,
          }
        );
      }

      const data = response.data;

      if (
        data?.app !== "pos" ||
        data?.subdomain !== company ||
        !data?.access_token ||
        !data?.session_id ||
        !data?.expires_at
      ) {
        throw new Error(
          "Respuesta de autenticación inválida."
        );
      }

      try {
        savePosSession(company, data);
      } catch {
        let released = false;

        try {
          await api.post(
            "/auth/pos/logout",
            {},
            {
              headers: {
                Authorization: `Bearer ${data.access_token}`,
              },
            }
          );

          released = true;
        } catch {
          // El servidor conserva el vencimiento de la sesión.
        }

        throw new Error(
          "El navegador no permitió guardar la sesión POS. " +
          "Revisa que el almacenamiento esté habilitado." +
          (
            released
              ? ""
              : " Tampoco se pudo confirmar el cierre " +
              "de la sesión creada en el servidor."
          )
        );
      }

      setPassword("");

      navigate(
        `/${encodeURIComponent(company)}/ventas`,
        {
          replace: true,
        }
      );
    } catch (loginError) {
      setError(
        loginError.response
          ? errorText(loginError)
          : loginError.message ||
          "No fue posible iniciar sesión."
      );
    } finally {
      loginInProgress.current = false;
      setSaving(false);
    }
  }


  const pageStyle = {
    "--pos-primary": primaryColor,
    "--pos-secondary": secondaryColor,
    "--pos-background": backgroundColor,
  };


  if (loading || checkingSession) {
    return (
      <main
        className="pos-login-page"
        style={pageStyle}
      >
        <section
          className="pos-login-status"
          role="status"
          aria-live="polite"
        >
          <span
            className="pos-login-spinner"
            aria-hidden="true"
          />

          <h1>
            {loading
              ? "Cargando empresa"
              : "Validando sesión"}
          </h1>

          <p>
            {loading
              ? "Preparando tu punto de venta…"
              : "Comprobando tu sesión POS…"}
          </p>
        </section>
      </main>
    );
  }


  if (companyError) {
    return (
      <main
        className="pos-login-page"
        style={pageStyle}
      >
        <section className="pos-login-status">
          <span className="pos-login-eyebrow">
            NETLEDGER POS
          </span>

          <h1>
            No pudimos cargar la empresa
          </h1>

          <p role="alert">
            {companyError}
          </p>

          <button
            type="button"
            className="pos-login-submit"
            onClick={() =>
              window.location.reload()
            }
          >
            Reintentar
          </button>
        </section>
      </main>
    );
  }


  return (
    <main
      className="pos-login-page"
      style={pageStyle}
    >
      <section
        className="pos-login-container"
        aria-labelledby="pos-login-title"
      >
        <div className="pos-login-form-panel">
          <form
            className="pos-login-form"
            onSubmit={handleLogin}
            aria-busy={saving}
          >
            <header className="pos-login-header">
              <span className="pos-login-eyebrow">
                NETLEDGER POS
              </span>

              <h1 id="pos-login-title">
                {name}
              </h1>

              <p>
                Inicia sesión para acceder
                a tu punto de venta.
              </p>
            </header>

            <div className="pos-login-field">
              <input
                id="pos-email"
                name="email"
                type="email"
                placeholder=" "
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                value={email}
                onChange={(event) =>
                  setEmail(event.target.value)
                }
                required
                disabled={saving}
              />

              <label htmlFor="pos-email">
                Correo electrónico
              </label>
            </div>

            <div className="pos-login-field pos-login-password-field">
              <input
                id="pos-password"
                name="password"
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                placeholder=" "
                autoComplete="current-password"
                value={password}
                onChange={(event) =>
                  setPassword(event.target.value)
                }
                required
                disabled={saving}
              />

              <label htmlFor="pos-password">
                Contraseña
              </label>

              <button
                className="pos-login-password-toggle"
                type="button"
                onClick={() =>
                  setShowPassword(
                    (current) => !current
                  )
                }
                aria-label={
                  showPassword
                    ? "Ocultar contraseña"
                    : "Mostrar contraseña"
                }
                aria-pressed={showPassword}
                aria-controls="pos-password"
                disabled={saving}
              >
                {showPassword
                  ? "Ocultar"
                  : "Ver"}
              </button>
            </div>

            {sessionExpired && (
              <div
                className="pos-login-error"
                role="status"
              >
                Tu sesión POS terminó o dejó de
                ser válida. Inicia sesión nuevamente.
              </div>
            )}

            {error && (
              <div
                className="pos-login-error"
                role="alert"
              >
                {error}
              </div>
            )}

            <button
              className="pos-login-submit"
              disabled={
                saving ||
                checkingSession ||
                !companyData
              }
              type="submit"
            >
              {saving && (
                <span
                  className="pos-login-spinner pos-login-spinner-small"
                  aria-hidden="true"
                />
              )}

              {saving
                ? "Ingresando…"
                : "Ingresar al POS"}
            </button>

            <p className="pos-login-form-note">
              Acceso para usuarios autorizados
              de {name}.
            </p>
          </form>
        </div>

        <aside className="pos-login-brand-panel">
          <div className="pos-login-brand">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt={`Logo de ${name}`}
                className="pos-login-logo"
                onError={() =>
                  setLogoFailed(true)
                }
              />
            ) : (
              <div
                className="pos-login-logo-placeholder"
                aria-hidden="true"
              >
                {name.charAt(0).toUpperCase()}
              </div>
            )}

            <div className="pos-login-brand-copy">
              <span className="pos-login-brand-label">
                PUNTO DE VENTA
              </span>

              <h2>
                Bienvenido a tu POS
              </h2>

              <p>
                Gestiona tus ventas y atiende a
                tus clientes desde un solo lugar.
              </p>
            </div>
          </div>
        </aside>
      </section>
      {showSessionConfirm && (
        <POSSessionConfirm
          companyName={name}
          primaryColor={primaryColor}
          onConfirm={acceptSessionConfirm}
          onCancel={cancelSessionConfirm}
        />
      )}
      <footer className="pos-login-footer">
        © {new Date().getFullYear()} NetLedger POS · SYNTRIXA S.A.S.
      </footer>
    </main>
  );
}